/**
 * Geolocation utility functions for distance calculations and location management
 */

/**
 * Calculate the distance between two coordinates using the Haversine formula
 * @param {number} lat1 - Latitude of first point
 * @param {number} lon1 - Longitude of first point
 * @param {number} lat2 - Latitude of second point
 * @param {number} lon2 - Longitude of second point
 * @returns {number} Distance in kilometers
 */
export function calculateDistance(lat1, lon1, lat2, lon2) {
  // Validate input coordinates
  if (typeof lat1 !== 'number' || typeof lon1 !== 'number' || 
      typeof lat2 !== 'number' || typeof lon2 !== 'number') {
    throw new Error('All coordinates must be numbers');
  }

  if (isNaN(lat1) || isNaN(lon1) || isNaN(lat2) || isNaN(lon2)) {
    throw new Error('All coordinates must be valid numbers');
  }

  // Check coordinate ranges
  if (lat1 < -90 || lat1 > 90 || lat2 < -90 || lat2 > 90) {
    throw new Error('Latitude must be between -90 and 90 degrees');
  }

  if (lon1 < -180 || lon1 > 180 || lon2 < -180 || lon2 > 180) {
    throw new Error('Longitude must be between -180 and 180 degrees');
  }

  // Earth's radius in kilometers
  const R = 6371;

  // Convert degrees to radians
  const dLat = toRadians(lat2 - lat1);
  const dLon = toRadians(lon2 - lon1);

  // Haversine formula
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) *
            Math.sin(dLon / 2) * Math.sin(dLon / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  // Distance in kilometers
  const distance = R * c;

  return distance;
}

/**
 * Convert degrees to radians
 * @param {number} degrees - Angle in degrees
 * @returns {number} Angle in radians
 */
function toRadians(degrees) {
  return degrees * (Math.PI / 180);
}

/**
 * Check if user has moved more than the specified distance from cached location
 * @param {Object} currentCoords - Current coordinates {lat, lon}
 * @param {Object} cachedCoords - Cached coordinates {lat, lon}
 * @param {number} maxDistance - Maximum allowed distance in kilometers (default: 1)
 * @returns {boolean} True if user has moved more than maxDistance
 */
export function hasUserMoved(currentCoords, cachedCoords, maxDistance = 1) {
  if (!currentCoords || !cachedCoords) {
    return true; // Consider as moved if either coordinate set is missing
  }

  if (!currentCoords.lat || !currentCoords.lon || 
      !cachedCoords.lat || !cachedCoords.lon) {
    return true; // Consider as moved if any coordinate is missing
  }

  try {
    const distance = calculateDistance(
      currentCoords.lat, 
      currentCoords.lon,
      cachedCoords.lat, 
      cachedCoords.lon
    );

    return distance > maxDistance;
  } catch (error) {
    // If there's an error calculating distance, consider as moved
    return true;
  }
}

/**
 * Validate coordinates object
 * @param {Object} coords - Coordinates object {lat, lon}
 * @returns {boolean} True if coordinates are valid
 */
export function validateCoordinates(coords) {
  if (!coords || typeof coords !== 'object') {
    return false;
  }

  if (typeof coords.lat !== 'number' || typeof coords.lon !== 'number') {
    return false;
  }

  if (isNaN(coords.lat) || isNaN(coords.lon)) {
    return false;
  }

  if (coords.lat < -90 || coords.lat > 90) {
    return false;
  }

  if (coords.lon < -180 || coords.lon > 180) {
    return false;
  }

  return true;
}

/**
 * Format coordinates for display
 * @param {Object} coords - Coordinates object {lat, lon}
 * @param {number} precision - Number of decimal places (default: 4)
 * @returns {string} Formatted coordinates string
 */
export function formatCoordinates(coords, precision = 4) {
  if (!validateCoordinates(coords)) {
    return 'Invalid coordinates';
  }

  const lat = coords.lat.toFixed(precision);
  const lon = coords.lon.toFixed(precision);

  return `${lat}, ${lon}`;
}
